import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import posthog from 'posthog-js'
import InfiniteScroll from 'react-infinite-scroll-component'
import Header from 'components/headers/bulletins'
import DotsLoading from 'components/loadings/dotsLoading'
import SettlementConfirmModal from 'components/modals/settlementConfirm'
import SettlementDebtModal from 'components/modals/settlementDebt'
import useGetAdminSettlements from './hooks/useGetAdminSettlements'
import useCheckoutStatus from './hooks/useCheckoutStatus'
import useSettleAndExit, { SettleMode } from './hooks/useSettleAndExit'
import { settlementPlan, SettlementPlan } from './settlementPlan'
import SettleSheet from './settleSheet'
import Item from './item'

/**
 * درخواست‌های تسویه و خروج — the manager's side.
 *
 * «قبول» is a three-call sequence, not a button: read what the member's
 * account comes to, settle it one way or the other, then stamp the request.
 * The page owns that sequence because only it knows both the request and the
 * fund; the modals below are the steps it walks through.
 */
export default function AdminSettlement() {
    const { moneyPool } = useParams()
    const [selectedItem, setSelectedItem] = useState<
        IAdminSettlementResult | undefined
    >()
    const [plan, setPlan] = useState<SettlementPlan | undefined>()
    const [step, setStep] = useState<
        'confirm' | 'payout' | 'debt' | undefined
    >()

    const { settlements, isLoadingSettlements, fetchNextPage, refetch } =
        useGetAdminSettlements()

    const close = useCallback(() => {
        setStep(undefined)
        setPlan(undefined)
        setSelectedItem(undefined)
    }, [])

    const { isSettling, settle } = useSettleAndExit(() => {
        close()
        refetch()
    })

    const runSettle = useCallback(
        (mode: SettleMode, amount: number, bankAccount?: number) => {
            if (!selectedItem) return
            settle({
                moneyPool,
                poolShip: selectedItem.requesting_user.id,
                requestId: selectedItem.id,
                mode,
                amount,
                bankAccount,
            })
        },
        [selectedItem, moneyPool, settle]
    )

    const { isCheckingStatus, checkStatus } = useCheckoutStatus((status) => {
        const next = settlementPlan(status, selectedItem?.moneypool)
        setPlan(next)
        posthog.capture('exit_request_plan_decided', {
            moneypool_id: moneyPool,
            kind: next.kind,
            amount: next.kind === 'ready' ? 0 : next.amount,
        })
        if (next.kind === 'ready') {
            // nothing owed either way: settle and remove in one go, exactly
            // what the manager asked for when they tapped قبول
            setStep(undefined)
            // no account: there is nothing to pay, and the checkout endpoint
            // validates the account it is given (active + verified). Sending
            // one we do not need would let an unverified card block a
            // settlement that involves no money at all.
            runSettle('none', 0)
            return
        }
        setStep(next.kind === 'debt' ? 'debt' : 'payout')
    })

    const onWatchEnd = useCallback(() => {
        fetchNextPage()
    }, [fetchNextPage])

    const selectedName = selectedItem
        ? `${selectedItem.requesting_user.member.first_name} ${selectedItem.requesting_user.member.last_name}`
        : ''

    return (
        <>
            <div className=" relative min-h-screen">
                <Header title="درخواست تسویه و خروج" />
                {isLoadingSettlements ? (
                    <DotsLoading />
                ) : (
                    <InfiniteScroll
                        next={onWatchEnd}
                        hasMore={
                            settlements
                                ? settlements?.pages[
                                      settlements.pages.length - 1
                                  ].next
                                    ? true
                                    : false
                                : false
                        }
                        dataLength={
                            settlements
                                ? settlements.pages.flatMap((x) => x.results)
                                      .length
                                : 0
                        }
                        loader={<DotsLoading />}
                    >
                        <div>
                            {settlements &&
                                settlements.pages
                                    .flatMap((x) => x.results)
                                    .map((item) => (
                                        <Item
                                            item={item}
                                            refetch={refetch}
                                            key={item.id}
                                            isBusy={isSettling}
                                            onAccept={(accepted) => {
                                                posthog.capture(
                                                    'exit_request_accept_clicked',
                                                    { moneypool_id: moneyPool }
                                                )
                                                setSelectedItem(accepted)
                                                setStep('confirm')
                                            }}
                                        />
                                    ))}
                        </div>
                    </InfiniteScroll>
                )}
            </div>

            <SettlementConfirmModal
                show={step === 'confirm'}
                onHide={close}
                name={selectedName}
                isChecking={isCheckingStatus}
                onConfirm={() =>
                    selectedItem &&
                    checkStatus({
                        moneyPool,
                        poolShip: selectedItem.requesting_user.id,
                    })
                }
            />

            <SettlementDebtModal
                show={step === 'debt'}
                onHide={close}
                name={selectedName}
                amount={plan?.kind === 'debt' ? plan.amount : 0}
            />

            {step === 'payout' && plan?.kind === 'payout' && (
                <SettleSheet
                    isOpen
                    onDismiss={close}
                    request={selectedItem}
                    plan={plan}
                    isSettling={isSettling}
                    onSettle={(mode, bankAccount) =>
                        runSettle(mode, plan.amount, bankAccount)
                    }
                />
            )}
        </>
    )
}
